import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdMinimize } from 'react-icons/md';
import { toast } from 'react-hot-toast';

import {
  Calendar,
  Clock,
  Users,
  Paperclip,
  X,
  Bold,
  Italic,
  List,
  Link as LinkIcon,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  CheckCircle2,
  AlertCircle,
  Maximize2,
  FolderKanban,
  Tags,
  ChevronDown,
  Clock3,
  Users2,
  BarChart2,
  Calendar as CalendarIcon,
  ArrowLeft,
  GitBranch,
  Building2,
  Crown,
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import { Toaster } from 'react-hot-toast';
import { notifyTaskAssignees } from '../../lib/notificationService';
import { createActivity } from '../../lib/activityService';

// Supabase client'ı oluşturun
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// Dosya boyutunu formatla (B, KB, MB, GB)
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const CreateTask = () => {
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_id: '',
    sub_project_id: '',
    category_id: [],
    priority: 'medium',
    start_date: '',
    end_date: '',
    notes: '',
    consulting_id: '',
    consulting_project_id: '',
  });

  // Özel proje tipleri için kontrol
  const SPECIAL_PROJECT_TYPES = ['Genel', 'Muhasebe', 'Finans'];

  // Kullanıcı rolü için yeni state
  const [userRole, setUserRole] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [isRoleLoading, setIsRoleLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [toastShown, setToastShown] = useState(false);

  // Seçili projenin özel tip olup olmadığını kontrol et
  const isSpecialProjectType = (projectId) => {
    const selectedProject = projects.find(p => p.id === projectId);
    return selectedProject && SPECIAL_PROJECT_TYPES.includes(selectedProject.name);
  };

  // Validation states
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('');
  const [uploadDetails, setUploadDetails] = useState({
    speed: 0,          // KB/s cinsinden hız
    uploaded: 0,       // Yüklenen bayt
    total: 0,          // Toplam bayt
    timeLeft: 0,       // Tahmini kalan süre (saniye)
    currentFile: '',   // Şu anda yüklenen dosya
    fileIndex: 0,      // Dosya indeksi
    totalFiles: 0      // Toplam dosya sayısı
  });
  const lastUploadedRef = useRef({ time: Date.now(), bytes: 0 }); // Hız hesaplaması için

  // Diğer state'ler
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [files, setFiles] = useState([]);
  const [priority, setPriority] = useState('medium');
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]); // DB'den çekilen kullanıcılar
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const MEMBERS_TO_SHOW = 3; // Başlangıçta gösterilecek üye sayısı

  // Yeni state'ler
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProjectForModal, setSelectedProjectForModal] = useState(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [subProjects, setSubProjects] = useState({});

  // Add new state for managing the category selection UI
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  // Yeni state'ler
  const [consultingFirms, setConsultingFirms] = useState([]);
  const [consultingProjects, setConsultingProjects] = useState({});

  // İlk yüklemede danışmanlık firmalarını getir
  useEffect(() => {
    fetchConsultingFirms();
    fetchUserRole(); // Kullanıcı rolünü al
  }, []);

  // Realtime yetki kontrolü için subscription
  useEffect(() => {
    if (!currentUserId) return;

    // Kullanıcı profilindeki değişiklikleri dinle
    const subscription = supabase
      .channel('public:user_profiles')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles',
        filter: `id=eq.${currentUserId}`,
      }, (payload) => {
        // Rol değişirse, yetkilendirmeyi güncelle
        if (payload.new && payload.new.role) {
          const newRole = payload.new.role;
          setUserRole(newRole);
          
          // Yetki durumunu güncelle
          if (newRole === 'admin' || newRole === 'super') {
            setHasPermission(true);
            toast.success('Görev oluşturma yetkiniz aktif edildi.');
          } else {
            setHasPermission(false);
            if (!toastShown) {
              toast.error('Görev oluşturma yetkiniz kaldırıldı.');
              setToastShown(true);
            }
          }
        }
      })
      .subscribe();

    // Cleanup
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [currentUserId, toastShown]);

  // Kullanıcı rolünü getiren fonksiyon
  const fetchUserRole = async () => {
    setIsRoleLoading(true);
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('Kullanıcı bilgisi alınamadı:', error);
        setHasPermission(false);
        return;
      }
      
      if (!user) {
        setHasPermission(false);
        return;
      }
      
      setCurrentUserId(user.id);
      
      // Kullanıcının rolünü al
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single();
      
      if (profileError) {
        console.error('Kullanıcı profili alınamadı:', profileError);
        setHasPermission(false);
        return;
      }
      
      setUserRole(userProfile.role);
      
      // Sadece admin veya super rolü olan kullanıcılar görev oluşturabilir
      if (userProfile.role === 'admin' || userProfile.role === 'super') {
        setHasPermission(true);
      } else {
        setHasPermission(false);
        if (!toastShown) {
          // toast.error('Görev oluşturmak için yeterli yetkiniz bulunmamaktadır.');
          setToastShown(true);
        }
      }
    } catch (error) {
      console.error('Kullanıcı rolü alınırken hata:', error);
      setHasPermission(false);
    } finally {
      setIsRoleLoading(false);
    }
  };

  // Danışmanlık firmalarını getir
  const fetchConsultingFirms = async () => {
    try {
      const { data, error } = await supabase
        .from('consulting')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setConsultingFirms(data || []);
    } catch (error) {
      console.error('Error fetching consulting firms:', error);
    }
  };

  // Seçilen firmanın projelerini getir
  const fetchConsultingProjects = async (firmId) => {
    try {
      const { data, error } = await supabase
        .from('consulting_sub_projects')
        .select(`
          id,
          name,
          description,
          status,
          team_size,
          task_count,
          completed_tasks,
          start_date,
          end_date
        `)
        .eq('parent_consulting_id', firmId);
      
      if (error) throw error;
      setConsultingProjects(prev => ({
        ...prev,
        [firmId]: data || []
      }));
    } catch (error) {
      console.error('Error fetching consulting projects:', error);
      toast.error('Danışmanlık projeleri yüklenirken bir hata oluştu');
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Eğer danışmanlık firması seçildiyse projelerini getir
    if (name === 'consulting_id' && value) {
      fetchConsultingProjects(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        consulting_project_id: '',
      }));
    }

    // Eğer proje değiştiyse ve özel tip ise, alt proje ve kategori seçimlerini sıfırla
    if (name === 'project_id') {
      if (value === 'consulting') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          sub_project_id: '',
          category_id: [],
          consulting_id: '',
          consulting_project_id: ''
        }));
        setSelectedCategories([]);
      } else if (isSpecialProjectType(value)) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          sub_project_id: '',
          category_id: []
        }));
        setSelectedCategories([]);
      } else if (value) {
        fetchSubProjects(value);
        setFormData(prev => ({
          ...prev,
          [name]: value,
          sub_project_id: ''
        }));
      }
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Add this function to handle date changes with validation
  const handleDateChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user fixes the date
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Başlık her durumda zorunlu
    if (!formData.title.trim()) {
      newErrors.title = 'Görev başlığı zorunludur';
    }

    // Başlangıç ve bitiş tarihleri her durumda zorunlu
    if (!formData.start_date) {
      newErrors.start_date = 'Başlangıç tarihi zorunludur';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'Bitiş tarihi zorunludur';
    }

    if (formData.start_date && formData.end_date && new Date(formData.start_date) > new Date(formData.end_date)) {
      newErrors.end_date = 'Bitiş tarihi başlangıç tarihinden önce olamaz';
    }

    // Proje seçimi kontrolü
    if (!formData.project_id) {
      newErrors.project_id = 'Proje seçimi zorunludur';
    } else {
      // Danışmanlık seçiliyse
      if (formData.project_id === 'consulting') {
        if (!formData.consulting_id) {
          newErrors.consulting_id = 'Danışmanlık firması seçimi zorunludur';
        }
        if (!formData.consulting_project_id) {
          newErrors.consulting_project_id = 'Firma projesi seçimi zorunludur';
        }
      }
      // Normal proje seçiliyse - kategori seçimi artık zorunlu değil
      // console.log("Kategori durumu:", formData.category_id);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Upload files to Supabase Storage
  const uploadFiles = async (taskId) => {
    const uploadedFiles = [];
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    let uploadedSize = 0;
    
    setUploadDetails({
      speed: 0,
      uploaded: 0,
      total: totalSize,
      timeLeft: 0,
      currentFile: '',
      fileIndex: 0,
      totalFiles: files.length
    });
    
    lastUploadedRef.current = { time: Date.now(), bytes: 0 };

    // Create a separate Supabase client for file operations
    const fileSupabase = createClient(
      import.meta.env.VITE_FILE_SUPABASE_URL,
      import.meta.env.VITE_FILE_SUPABASE_ANON_KEY
    );

    // Türkçe karakterleri ve özel karakterleri temizleme fonksiyonu
    const sanitizeFileName = (fileName) => {
      // Türkçe karakterleri değiştir
      const turkishChars = { 
        'ç': 'c', 'Ç': 'C', 
        'ğ': 'g', 'Ğ': 'G', 
        'ı': 'i', 'İ': 'I', 
        'ö': 'o', 'Ö': 'O', 
        'ş': 's', 'Ş': 'S', 
        'ü': 'u', 'Ü': 'U'
      };
      
      let sanitized = fileName;
      
      // Türkçe karakterleri değiştir
      Object.keys(turkishChars).forEach(char => {
        sanitized = sanitized.replace(new RegExp(char, 'g'), turkishChars[char]);
      });
      
      // Özel karakterleri kaldır (dosya sistemine zarar verebilecek karakterler)
      // Daha kapsamlı bir liste ile tüm problemli karakterleri kaldır
      sanitized = sanitized.replace(/[*?:"<>|\\\/,{}()\[\]#%&~+=;]/g, '');
      
      // Boşlukları alt çizgi ile değiştir
      sanitized = sanitized.replace(/\s+/g, '_');
      
      // Dosya adını küçük harfe çevir
      sanitized = sanitized.toLowerCase();
      
      // Timestamp ekle (benzersiz isim için)
      const timestamp = new Date().getTime();
      
      // "teira3d_" ön ekini ve timestamp ekle
      return `teira3d_${timestamp}_${sanitized}`;
    };

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExt = file.name.split('.').pop();
      const baseName = file.name.substring(0, file.name.lastIndexOf('.'));
      const sanitizedBaseName = sanitizeFileName(baseName);
      const fileName = `${taskId}/${sanitizedBaseName}.${fileExt}`;
      
      setProcessingStage(`Dosya yükleniyor`);
      setUploadDetails(prev => ({
        ...prev,
        currentFile: file.name,
        fileIndex: i + 1
      }));

      const { data, error } = await fileSupabase.storage
        .from(import.meta.env.VITE_FILE_TASKS_SUPABASE_BUCKET_NAME)
        .upload(fileName, file, {
          onUploadProgress: (progress) => {
            // Geçen süre ve yüklenen bayt farkını hesapla
            const now = Date.now();
            const timeDiff = (now - lastUploadedRef.current.time) / 1000; // saniye cinsinden
            const bytesDiff = progress.loaded - lastUploadedRef.current.bytes;
            
            // 0.5 saniyede bir hız hesaplaması yap
            if (timeDiff > 0.5) {
              const speedKBps = Math.round((bytesDiff / timeDiff) / 1024); // KB/s
              
              // Kalan süreyi hesapla
              const remainingBytes = progress.total - progress.loaded;
              const timeLeftSec = speedKBps > 0 ? Math.round(remainingBytes / (speedKBps * 1024)) : 0;
              
              // State güncelle
              setUploadDetails(prev => ({
                ...prev,
                speed: speedKBps,
                uploaded: uploadedSize + progress.loaded,
                total: totalSize,
                timeLeft: timeLeftSec
              }));
              
              // Referans değerlerini güncelle
              lastUploadedRef.current = { time: now, bytes: progress.loaded };
            }
            
            // Her bir dosya için bireysel ilerleme 
            const progressPercentage = (progress.loaded / progress.total) * 100;
            
            // Toplam ilerleme - önceki dosyalar + şu anki dosya
            const totalUploaded = uploadedSize + progress.loaded;
            const overallProgress = (totalUploaded / totalSize) * 100;
            setUploadProgress(Math.round(overallProgress));
          },
          cacheControl: '3600'
        });

      if (error) {
        console.error('Error uploading file:', error);
        continue;
      }

      // Dosyayı başarıyla yükledikten sonra, yüklenen boyutu güncelle
      uploadedSize += file.size;
      setUploadProgress(Math.round((uploadedSize / totalSize) * 100));
      setUploadDetails(prev => ({
        ...prev,
        uploaded: uploadedSize,
        speed: 0 // Her dosya sonunda hızı sıfırla
      }));

      const { data: publicUrl } = fileSupabase.storage
        .from(import.meta.env.VITE_FILE_TASKS_SUPABASE_BUCKET_NAME)
        .getPublicUrl(fileName);

      uploadedFiles.push({
        name: file.name,
        size: file.size,
        type: file.type,
        url: publicUrl.publicUrl,
      });
    }

    return uploadedFiles;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
     
    // Yetki kontrolü
    if (!hasPermission) {
      toast.error('Görev oluşturmak için admin veya süper yönetici olmalısınız.');
      return;
    }
    
    // İşleme aşamasını başlat ve form doğrulama mesajını göster
    setIsSubmitting(true);
    setProcessingStage('Form doğrulanıyor...');

    if (!validateForm()) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      setIsSubmitting(false);
      setProcessingStage('');
      return;
    }

    try {
      setProcessingStage('Kullanıcı bilgileri doğrulanıyor...');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }

      // Prepare task data
      setProcessingStage('Görev verileri hazırlanıyor...');
      
      // Başlangıç tarihine göre status belirle
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Günün başlangıcına ayarla
      
      const startDate = new Date(formData.start_date);
      startDate.setHours(0, 0, 0, 0); // Günün başlangıcına ayarla
      
      // Başlangıç tarihi bugünden ileride ise "todo", bugün veya öncesinde ise "in_progress"
      const taskStatus = startDate > currentDate ? 'todo' : 'in_progress';
      
      const taskData = {
        title: formData.title,
        description: formData.description,
        priority: priority, // formData.priority yerine
        start_date: formData.start_date,
        end_date: formData.end_date,
        notes: formData.notes,
        status: taskStatus, // Dinamik status ataması
        assigned_members: selectedMembers.map(member => ({
          id: member.id,
          name: `${member.first_name} ${member.last_name}`,
          avatar: member.profile_photo_url,
        })),
        created_by: user.id,
      };
      

      // Consulting projesi için
      if (formData.project_id === 'consulting') {
        taskData.consulting_project_id = formData.consulting_project_id;
        taskData.consulting_parent_id = formData.consulting_id;
        taskData.project_id = null;
        taskData.sub_project_id = null;
      } else {
        taskData.project_id = formData.project_id;
        taskData.sub_project_id = formData.sub_project_id || null;
        taskData.consulting_project_id = null;
        taskData.consulting_parent_id = null;
      }

      // Create task
      setProcessingStage('Görev veritabanına kaydediliyor...');
      const { data: task, error: taskError } = await supabase
        .from('tasks')
        .insert([taskData])
        .select()
        .single();

      if (taskError) throw taskError;

      // Insert task-category relationships if not consulting
      if (formData.project_id !== 'consulting' && selectedCategories.length > 0) {
        setProcessingStage('Kategori bilgileri kaydediliyor...');
        const { error: categoryError } = await supabase
          .from('task_categories')
          .insert(
            selectedCategories.map(category => ({
              task_id: task.id,
              category_id: category.id
            }))
          );

        if (categoryError) throw categoryError;
      }

      // Upload files if any
      if (files.length > 0) {
        setProcessingStage('Dosyalar yükleniyor...');
        const uploadedFiles = await uploadFiles(task.id);
        
        // Update task with attachment URLs
        setProcessingStage('Görev ekleri güncelleniyor...');
        const { error: updateError } = await supabase
          .from('tasks')
          .update({ attachments: uploadedFiles })
          .eq('id', task.id);

        if (updateError) throw updateError;
      }

      // Kullanıcı profilini al (bildirimler için)
      setProcessingStage('Bildirimler hazırlanıyor...');
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();
        
      if (profileError) {
        console.error('Profil bilgileri alınamadı:', profileError);
      } else {
        const creatorName = `${userProfile.first_name} ${userProfile.last_name}`;
        
        // Aktivite kaydı oluştur
        setProcessingStage('Aktivite kaydı oluşturuluyor...');
        try {
          await createActivity(
            user.id,
            task.id,
            'create',
            `${creatorName} tarafından "${task.title}" görevi oluşturuldu`
          );
        } catch (activityError) {
          console.error('Aktivite kaydı oluşturma hatası:', activityError);
        }

        // Atanan üyelere bildirim gönder
        setProcessingStage('Bildirimler gönderiliyor...');
        try {
          await notifyTaskAssignees(task, user.id, creatorName);
        } catch (notificationError) {
          console.error('Bildirim gönderme hatası:', notificationError);
        }
      }

      setProcessingStage('Görev başarıyla oluşturuldu!');
      toast.success('Görev başarıyla oluşturuldu');
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        project_id: '',
        sub_project_id: '',
        category_id: [],
        priority: 'medium',
        start_date: '',
        end_date: '',
        notes: '',
        consulting_id: '',
        consulting_project_id: '',
      });
      setSelectedMembers([]);
      setFiles([]);
      setEditorContent('');

    } catch (error) {
      console.error('Görev oluşturma hatası:', error);
      toast.error('Görev oluşturulurken bir hata oluştu');
    } finally {
      // İşlemi tamamla ve durumları sıfırla
      setTimeout(() => {
        setIsSubmitting(false);
        setProcessingStage('');
        setUploadProgress(0);
      }, 1500); // Başarı mesajını 1.5 saniye görüntüle
    }
  };

  // Dosya seçme ve silme işlemleri
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  // Üye seçimini yönet
  const toggleMember = (member) => {
    if (selectedMembers.find((m) => m.id === member.id)) {
      setSelectedMembers(selectedMembers.filter((m) => m.id !== member.id));
    } else {
      setSelectedMembers([...selectedMembers, member]);
    }
  };

  // Kullanıcıları DB'den çekme fonksiyonu
  const fetchTeamMembers = async () => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, first_name, last_name, job_position, profile_photo_url, online_status');
    if (error) {
      console.error('Error fetching team members:', error);
    } else {
      setTeamMembers(data);
    }
  };

  // İlk sorgu ve her 5 saniyede bir güncelleme
  useEffect(() => {
    // İlk yüklemede tüm verileri çek
    const fetchAllData = async () => {
      await Promise.all([
        fetchTeamMembers(),
        fetchCategories(),
        fetchProjects(),
        // Alt projeleri sadece bir proje seçiliyse çek
        selectedProjectForModal?.id ? fetchSubProjects(selectedProjectForModal.id) : Promise.resolve()
      ]);
    };

    // İlk yükleme
    fetchAllData();

    // Her 3 saniyede bir güncelle
    const intervalId = setInterval(() => {
      fetchAllData();
    }, 3000); // 3000 ms = 3 sn

    // Cleanup interval
    return () => clearInterval(intervalId);
  }, [selectedProjectForModal]); // selectedProjectForModal değiştiğinde de yeniden çalıştır

  // Arama yapıldığında veya üyeler listeleneceğinde filtreleme
  const filteredMembers = teamMembers.filter((member) => {
    const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
    return (
      fullName.includes(searchQuery.toLowerCase()) ||
      (member.job_position && member.job_position.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  // Profil resmi için varsayılan avatar (fotoğraf yoksa)
  const getDefaultAvatar = (firstName, lastName) => {
    const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=3b82f6&color=fff`;
  };

  // Kategorileri veritabanından çek
  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('*');
    if (error) {
      console.error('Error fetching categories:', error);
    } else {
      setCategories(data);
    }
  };

  // Projeleri veritabanından çek
  const fetchProjects = async () => {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_categories (
          categories (*)
        )
      `);
    if (error) {
      console.error('Error fetching projects:', error);
    } else {
      setProjects(data || []);
    }
  };

  // Alt projeleri getir
  const fetchSubProjects = async (projectId) => {
    const { data, error } = await supabase
      .from('sub_projects')
      .select('*')
      .eq('parent_project_id', projectId);
      
    if (error) {
      console.error('Error fetching sub-projects:', error);
      return;
    }
    
    setSubProjects(prev => ({
      ...prev,
      [projectId]: data
    }));
  };

  // Handle category selection
  const handleCategoryToggle = (category) => {
    // Danışmanlık projesi seçiliyse işlem yapma
    if (formData.project_id === 'consulting') {
      return;
    }

    const isSelected = selectedCategories.some(cat => cat.id === category.id);
    
    if (isSelected) {
      // Eğer zaten seçiliyse, seçimi kaldır
      setSelectedCategories([]);
      setFormData(prev => ({
        ...prev,
        category_id: []
      }));
    } else {
      // Yeni kategori seçildiğinde, önceki seçimleri temizle ve sadece yeni kategoriyi ekle
      setSelectedCategories([category]);
      setFormData(prev => ({
        ...prev,
        category_id: [category.id]
      }));
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="p-8">
        <button
                type="button"
                onClick={() => window.history.back()}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors mt-2 mb-12">
                <ArrowLeft className="w-5 h-5" />
                <span>Back To the Future</span>
        </button>
        
        {/* Dosya yükleme durumu göstergesi */}
        {isSubmitting && processingStage.includes('Dosya') && (
          <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-blue-700 dark:text-blue-400">
                {processingStage}
              </h3>
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                {uploadDetails.fileIndex}/{uploadDetails.totalFiles}
              </span>
            </div>
            
            <div className="mb-2">
              <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                {uploadDetails.currentFile}
              </div>
              
              <div className="w-full h-2.5 bg-blue-100 dark:bg-blue-800/50 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-blue-500 dark:bg-blue-400 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                  initial={{ width: "0%" }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ type: "spring", stiffness: 50 }}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 text-xs text-blue-600 dark:text-blue-400">
              <div>
                <span className="font-medium">Yükleme Hızı: </span>
                {uploadDetails.speed > 1024 
                  ? `${(uploadDetails.speed / 1024).toFixed(2)} MB/s`
                  : `${uploadDetails.speed} KB/s`
                }
              </div>
              <div className="text-center">
                <span className="font-medium">Yüklenen: </span>
                {formatBytes(uploadDetails.uploaded)} / {formatBytes(uploadDetails.total)}
              </div>
              <div className="text-right">
                <span className="font-medium">Kalan Süre: </span>
                {uploadDetails.timeLeft > 60 
                  ? `${Math.floor(uploadDetails.timeLeft / 60)}dk ${uploadDetails.timeLeft % 60}sn`
                  : `${uploadDetails.timeLeft} saniye`
                }
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-8 border-b border-gray-200 dark:border-gray-700 pb-4">
          <div className="flex items-center gap-4">
            
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Yeni Görev Oluştur
            </h2>
          </div>
          
          {/* Kullanıcı yetki bilgisi */}
          <div className="flex items-center gap-3">
            {isRoleLoading ? (
              <div className="animate-pulse px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg w-48 h-10"></div>
            ) : (
              <div className="relative group">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  userRole === 'super'
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400'
                    : hasPermission 
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' 
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                }`}>
                  {userRole === 'super' ? (
                    <Crown className="w-5 h-5" />
                  ) : hasPermission ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <div>
                    <span className="font-medium">
                      {userRole === 'super' ? 'Süper Yönetici' : 
                       userRole === 'admin' ? 'Admin Yetkili Kişi' : 
                       userRole === 'user' ? 'Yetkisiz Kişi' : 'Bilinmeyen Rol'}
                    </span>
                  </div>
                  <AlertCircle className="w-4 h-4 ml-1 cursor-help" />
                </div>
                
                {/* Tooltip */}
                <div className="absolute right-0 z-10 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 w-64 p-3 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {hasPermission ? (
                      <>
                        <p className="font-medium mb-1 text-green-600 dark:text-green-400">Görev Oluşturma Yetkiniz Var</p>
                        <ul className="list-disc list-inside space-y-1 mt-1 text-xs">
                          <li>Yeni görevler oluşturabilirsiniz</li>
                          <li>Görevlere ekip üyeleri atayabilirsiniz</li>
                          <li>Görev kategorilerini seçebilirsiniz</li>
                          <li>Dosya ekleyebilirsiniz</li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <p className="font-medium mb-1 text-red-600 dark:text-red-400">Görev Oluşturma Yetkiniz Yok</p>
                        <p className="text-xs mt-1">Görev oluşturmak için admin veya süper yönetici olmalısınız.</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => {
                setFormData({
                  title: '',
                  description: '',
                  project_id: '',
                  category_id: [],
                  priority: 'medium',
                  start_date: '',
                  end_date: '',
                  notes: '',
                  consulting_id: '',
                  consulting_project_id: '',
                });
                setSelectedMembers([]);
                setFiles([]);
                setEditorContent('');
                setErrors({});
              }}
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !hasPermission}
              onClick={handleSubmit}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 ${
                isSubmitting || !hasPermission ? 'bg-gray-400 opacity-50 cursor-not-allowed' : 'bg-primary-600'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{processingStage === 'Dosya yükleniyor' ? 'Dosyalar Yükleniyor...' : processingStage || 'İşleniyor...'}</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Görevi Oluştur
                </>
              )}
            </button>
          </div>
        </div>

        {!hasPermission && !isRoleLoading && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-700 dark:text-red-400">Yetersiz Yetki</h3>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Görev oluşturmak için admin veya süper yönetici olmalısınız. Lütfen yöneticinize başvurun.
                </p>
              </div>
            </div>
          </div>
        )}

        <form className={`space-y-8 ${!hasPermission ? 'opacity-50 pointer-events-none' : ''}`} onSubmit={handleSubmit}>
          {/* Proje ve Kategori Seçimi */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Kategori Seçimi
              </label>
              <div className="relative">
                <select
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border ${
                    errors.project_id
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                  } rounded-lg text-gray-900 dark:text-white appearance-none focus:ring-2 focus:border-transparent transition-colors`}
                >
                  <option value="">Proje Seçin</option>
                  <option value="consulting">Danışmanlık</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                <FolderKanban className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              {errors.project_id && (
                <p className="mt-1 text-sm text-red-500">{errors.project_id}</p>
              )}
            </div>

            {/* Danışmanlık seçiliyse firma ve proje seçimi göster */}
            {formData.project_id === 'consulting' && (
              <>
                {/* Firma Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Firma Seçimi
                  </label>
                  <div className="relative">
                    <select
                      name="consulting_id"
                      value={formData.consulting_id}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border ${
                        errors.consulting_id
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                      } rounded-lg text-gray-900 dark:text-white appearance-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors`}
                    >
                      <option value="">Firma Seçin</option>
                      {consultingFirms.map((firm) => (
                        <option key={firm.id} value={firm.id}>
                          {firm.name}
                        </option>
                      ))}
                    </select>
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  </div>
                  {errors.consulting_id && (
                    <p className="mt-1 text-sm text-red-500">{errors.consulting_id}</p>
                  )}
                </div>

                {/* Firma Projesi Seçimi */}
                {formData.consulting_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Firma Projesi
                    </label>
                    <div className="relative">
                      <select
                        name="consulting_project_id"
                        value={formData.consulting_project_id}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border ${
                          errors.consulting_project_id
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                        } rounded-lg text-gray-900 dark:text-white appearance-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors`}
                      >
                        <option value="">Proje Seçin</option>
                        {consultingProjects[formData.consulting_id]?.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                      <GitBranch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                    {errors.consulting_project_id && (
                      <p className="mt-1 text-sm text-red-500">{errors.consulting_project_id}</p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Alt Proje ve Kategori seçimleri sadece özel proje tipi değilse ve danışmanlık değilse göster */}
            {!isSpecialProjectType(formData.project_id) && formData.project_id && formData.project_id !== 'consulting' && (
              <>
                {/* Alt Proje Seçimi */}
                {subProjects[formData.project_id]?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {projects.find(p => p.id === formData.project_id)?.name === 'Bakım' ? 'Alt Başlık' : 'Alt Projeler'}
                    </label>
                    <div className="relative">
                      <select
                        name="sub_project_id"
                        value={formData.sub_project_id}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white appearance-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      >
                        <option value="">{projects.find(p => p.id === formData.project_id)?.name === 'Bakım' ? 'Alt Başlık Seçin' : 'Alt Proje Seçin'}</option>
                        {subProjects[formData.project_id].map((subProject) => (
                          <option key={subProject.id} value={subProject.id}>
                            {subProject.name}
                          </option>
                        ))}
                      </select>
                      <GitBranch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sol Kolon */}
            <div className="lg:col-span-2 space-y-6">
              {/* Başlık */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Görev Başlığı
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 rounded-lg border ${
                    errors.title
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                  } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent transition-colors`}
                  placeholder="Kısa ve açıklayıcı bir başlık girin"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>

              {/* Metin Düzenleyici */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Görev Açıklaması
                </label>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                  {/* Araç Çubuğu */}
                  <div className="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      <Bold className="w-4 h-4" />
                    </button>
                    <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      <Italic className="w-4 h-4" />
                    </button>
                    <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      <List className="w-4 h-4" />
                    </button>
                    <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-1" />
                    <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      <LinkIcon className="w-4 h-4" />
                    </button>
                    <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      <ImageIcon className="w-4 h-4" />
                    </button>
                    <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-1" />
                    <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      <AlignLeft className="w-4 h-4" />
                    </button>
                    <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      <AlignCenter className="w-4 h-4" />
                    </button>
                    <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                      <AlignRight className="w-4 h-4" />
                    </button>
                    <div className="flex-1" />
                    <button 
                      type="button" 
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      onClick={() => setIsEditorModalOpen(true)}
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Metin Alanı */}
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full p-4 min-h-[200px] max-h-[355px] h-[200px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-0 border-0 resize-vertical"
                    placeholder="Görev detaylarını açıklayın..."
                  />
                </div>
              </div>

              {/* Editor Modal */}
              {isEditorModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div 
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                    onClick={() => setIsEditorModalOpen(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="relative w-[80vw] h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
                  >
                    <div>
                  
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                    {/* Araç Çubuğu */}
                    <div className="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <Bold className="w-4 h-4" />
                      </button>
                      <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <Italic className="w-4 h-4" />
                      </button>
                      <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <List className="w-4 h-4" />
                      </button>
                      <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-1" />
                      <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <LinkIcon className="w-4 h-4" />
                      </button>
                      <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <ImageIcon className="w-4 h-4" />
                      </button>
                      <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-1" />
                      <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <AlignLeft className="w-4 h-4" />
                      </button>
                      <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <AlignCenter className="w-4 h-4" />
                      </button>
                      <button type="button" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <AlignRight className="w-4 h-4" />
                      </button>
                      <div className="flex-1" />
                      <button 
                        type="button" 
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        onClick={() => setIsEditorModalOpen(false)}
                      >
                        <MdMinimize className="w-4 h-4" />
                      </button>
                    </div>
                   
                  </div>
                </div>
                      <div className="p-4 h-[calc(80vh-80px)]">
                        <textarea
                          value={editorContent}
                          onChange={(e) => setEditorContent(e.target.value)}
                          className="w-full h-full p-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-0 border-0 resize-none"
                          placeholder="Görev detaylarını açıklayın..."
                        />
                      </div>
                    </motion.div>
                  </div>
                )}

                {/* Dosya Yükleme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ekler
                  </label>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const droppedFiles = Array.from(e.dataTransfer.files);
                      setFiles([...files, ...droppedFiles]);
                    }}
                    className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-300 ${
                      isDragging
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <motion.div
                        animate={{
                          scale: isDragging ? 1.1 : 1,
                          rotate: isDragging ? 180 : 0,
                        }}
                        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                      >
                        <Paperclip
                          className={`w-8 h-8 ${
                            isDragging ? 'text-primary-500' : 'text-gray-400'
                          } transition-colors`}
                        />
                      </motion.div>

                      <motion.div
                        animate={{
                          scale: isDragging ? 1.05 : 1,
                        }}
                        className="text-center mt-2"
                      >
                        <p
                          className={`text-sm ${
                            isDragging
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-gray-500 dark:text-gray-400'
                          } transition-colors mb-1`}
                        >
                          {isDragging ? (
                            <span className="font-medium">Dosyaları buraya bırakın</span>
                          ) : (
                            <span>
                              Dosyaları sürükleyip bırakın veya{' '}
                              <span className="text-primary-600 dark:text-primary-400">
                                seçin
                              </span>
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          PDF, DOCX, XLSX, JPG, PNG (max. 256GB)
                        </p>
                      </motion.div>

                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                      />

                      {!isDragging && (
                        <motion.label
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          htmlFor="file-upload"
                          className="mt-4 px-4 py-2 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-all"
                        >
                          Dosya Seç
                        </motion.label>
                      )}
                    </div>

                    {files.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-6 space-y-2"
                      >
                        {files.map((file, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`p-2 rounded-lg ${
                                  file.type.includes('image')
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                    : file.type.includes('pdf')
                                    ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                    : 'bg-gray-50 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400'
                                }`}
                              >
                                <Paperclip className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[200px]">
                                  {file.name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.1, rotate: 90 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => removeFile(index)}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                            >
                              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            </motion.button>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sağ Kolon */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Öncelik
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setPriority('low')}
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${
                        priority === 'low'
                          ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      } transition-colors`}
                    >
                      <span className="text-sm font-medium">Düşük</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPriority('medium')}
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${
                        priority === 'medium'
                          ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      } transition-colors`}
                    >
                      <span className="text-sm font-medium">Orta</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPriority('high')}
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${
                        priority === 'high'
                          ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      } transition-colors`}
                    >
                      <span className="text-sm font-medium">Yüksek</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Başlangıç Tarihi
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        id="start_date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleDateChange}
                        max={formData.end_date || undefined}
                        className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                          errors.start_date
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                        } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent transition-colors`}
                      />
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                    {errors.start_date && (
                      <p className="mt-1 text-sm text-red-500">{errors.start_date}</p>
                    )}
                  </div>

                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      setFormData(prev => ({
                        ...prev,
                        start_date: today
                      }));
                      // Clear error if exists
                      if (errors.start_date) {
                        setErrors(prev => ({
                          ...prev,
                          start_date: ''
                        }));
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Clock className="w-4 h-4" />
                    Bugün'ü Seç
                  </motion.button>

                  <div>
                    <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bitiş Tarihi
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        id="end_date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleDateChange}
                        min={formData.start_date || undefined}
                        className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                          errors.end_date
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                        } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent transition-colors`}
                      />
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                    {errors.end_date && (
                      <p className="mt-1 text-sm text-red-500">{errors.end_date}</p>
                    )}
                  </div>
                </div>

                {/* Ekip Üyeleri */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ekip Üyeleri
                  </label>
                  
                  <div className="relative mb-4">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="İsim veya pozisyon ara..."
                      className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    />
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    {searchQuery && (
                      <motion.button
                        whileHover={{ scale: 1.3 }}
                        whileTap={{ scale: 0.6 }}
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </motion.button>
                    )}
                  </div>

                  <div className="max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500 scrollbar-thumb-rounded-full scrollbar-track-rounded-full pl-2">
                    <div className="space-y-2 pr-2">
                      <style>
                        
                      </style>
                      {(searchQuery ? filteredMembers : teamMembers).map((member) => (
                        <motion.button
                          key={member.id}
                          type="button"
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => toggleMember(member)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg border ${
                            selectedMembers.find((m) => m.id === member.id)
                              ? 'bg-primary-50 border-primary-200 dark:bg-primary-900/20 dark:border-primary-800'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                          } transition-colors`}
                        >
                          <img
                            src={
                              member.profile_photo_url ||
                              getDefaultAvatar(member.first_name, member.last_name)
                            }
                            alt={`${member.first_name} ${member.last_name}`}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {member.first_name} {member.last_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {member.job_position || 'Pozisyon belirtilmemiş'}
                            </p>
                          </div>
                          <div
                            className={`w-2 h-2 rounded-full ${
                              member.online_status ? 'bg-green-400' : 'bg-red-600'
                            }`}
                          />
                        </motion.button>
                      ))}

                      {searchQuery && filteredMembers.length === 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-center py-4"
                        >
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            "{searchQuery}" için sonuç bulunamadı
                          </p>
                        </motion.div>
                      )}
                    </div>
                  </div>

                  {selectedMembers.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 flex items-center justify-between px-3 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg"
                    >
                      <span className="text-sm text-primary-600 dark:text-primary-400">
                        {selectedMembers.length} üye seçildi
                      </span>
                      <button
                        onClick={() => setSelectedMembers([])}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Tümünü Temizle
                      </button>
                    </motion.div>
                  )}
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notlar
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                    placeholder="Ek notlarınızı buraya ekleyin..."
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Projeler Listesi - Sadece admin ve super kullanıcılar görebilir */}
      {hasPermission && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
          <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Projeler
              </h3>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Toplam: {projects.length} Proje Mevcut
              </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <motion.div
                key={project.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setSelectedProjectForModal(project);
                  setIsProjectModalOpen(true);
                }}
                className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-base font-medium text-gray-900 dark:text-white">
                    {project.name}
                  </h4>
                  <div className={`px-2 py-1 text-xs font-medium rounded-full ${
                    project.status === 'active'
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                      : project.status === 'completed'
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                  }`}>
                    {project.status === 'active' ? 'Aktif' : project.status === 'completed' ? 'Tamamlandı' : 'Beklemede'}
                  </div>
                </div>
                
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">
                  {project.description || 'Açıklama bulunmuyor'}
                </p>

                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <Users2 className="w-4 h-4" />
                    <span>{project.team_size || 0} Üye</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BarChart2 className="w-4 h-4" />
                    <span>{project.task_count || 0} Görev</span>
                  </div>
                </div>

                {project.project_categories && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {project.project_categories.map(({ categories }) => (
                      <span
                        key={categories.id}
                        className={`px-2 py-1 text-xs font-medium rounded-full bg-${categories.color}-50 text-${categories.color}-700 dark:bg-${categories.color}-900/20 dark:text-${categories.color}-400`}
                      >
                        {categories.name}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Proje Detay Modal */}
      <AnimatePresence>
        {isProjectModalOpen && selectedProjectForModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsProjectModalOpen(false);
                setSelectedProjectForModal(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-3xl max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-6">

                  <div>
                <span className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-black to-gray-500 dark:from-gray-100 dark:to-gray-600 mb-2">Proje Detayları</span>

                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 mt-2">
                      {selectedProjectForModal.name}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        <span>Başlangıç: {new Date(selectedProjectForModal.start_date).toLocaleDateString('tr-TR')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock3 className="w-4 h-4" />
                        <span>Bitiş: {new Date(selectedProjectForModal.end_date).toLocaleDateString('tr-TR')}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsProjectModalOpen(false);
                      setSelectedProjectForModal(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Proje Açıklaması
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedProjectForModal.description || 'Açıklama bulunmuyor'}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Kategoriler
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProjectForModal.project_categories?.map(({ categories }) => (
                        <span
                          key={categories.id}
                          className={`px-3 py-1 text-sm font-medium rounded-full bg-${categories.color}-50 text-${categories.color}-700 dark:bg-${categories.color}-900/20 dark:text-${categories.color}-400`}
                        >
                          {categories.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Alt Projeler
                    </h4>
                    {subProjects[selectedProjectForModal.id]?.length > 0 ? (
                      <div className="grid grid-cols-1 gap-4">
                        {subProjects[selectedProjectForModal.id].map((subProject) => (
                          <div
                            key={subProject.id}
                            className="p-4 rounded-lg border border-gray-200 dark:border-gray-700"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                                {subProject.name}
                              </h5>
                              <div className={`px-2 py-1 text-xs font-medium rounded-full ${
                                subProject.status === 'active'
                                  ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                  : subProject.status === 'completed'
                                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                                  : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                              }`}>
                                {subProject.status === 'active' ? 'Aktif' : subProject.status === 'completed' ? 'Tamamlandı' : 'Beklemede'}
                              </div>
                            </div>
                            
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                              {subProject.description || 'Açıklama bulunmuyor'}
                            </p>

                            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                              <div className="flex items-center gap-1">
                                <Users2 className="w-3.5 h-3.5" />
                                <span>{subProject.team_size || 0} Üye</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <BarChart2 className="w-3.5 h-3.5" />
                                <span>{subProject.task_count || 0} Görev</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                <span>{subProject.completed_tasks || 0} Tamamlanan</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Bu projeye ait alt proje bulunmuyor.
                      </p>
                    )}
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Proje İstatistikleri
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Toplam Görev</div>
                        <div className="text-xl font-semibold text-gray-900 dark:text-white">
                          {selectedProjectForModal.task_count || 0}
                        </div>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Ekip Üyeleri</div>
                        <div className="text-xl font-semibold text-gray-900 dark:text-white">
                          {selectedProjectForModal.team_size || 0}
                        </div>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Tamamlanan</div>
                        <div className="text-xl font-semibold text-gray-900 dark:text-white">
                          {selectedProjectForModal.completed_tasks || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreateTask;



